import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { api, type CatalogScopeName } from './api';
import type { Dashboard, LiveBoard, MyEvent, MyOrder, PortalEvent, Sitting } from './types';

const DASH_CACHE = 'one10:dashboard';

function readCachedDashboard(): Dashboard | undefined {
  try {
    const raw = localStorage.getItem(DASH_CACHE);
    return raw ? (JSON.parse(raw) as Dashboard) : undefined;
  } catch {
    return undefined;
  }
}

/** The resident's orders and passes. Cached on the phone so passes still show without signal. */
export function useDashboard(enabled = true) {
  const q = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const d = await api.dashboard();
      try { localStorage.setItem(DASH_CACHE, JSON.stringify(d)); } catch { /* storage full or blocked */ }
      return d;
    },
    enabled,
    placeholderData: readCachedDashboard,
    refetchInterval: 30_000,
  });
  useRealtimeInvalidate(['orders', 'serves'], [['dashboard'], ['history']], enabled);
  return q;
}

/**
 * Which events' meals and menus a screen loads: residents' screens only need the events that are on; staff screens
 * (wrapped in a provider set to 'all') can pick a past event.
 */
export const CatalogScope = createContext<CatalogScopeName>('current');

export function usePublicData() {
  const scope = useContext(CatalogScope);
  const sittings = useQuery({ queryKey: ['sittings', scope], queryFn: () => api.sittings(scope), staleTime: 60_000 });
  const menu = useQuery({ queryKey: ['menu', scope], queryFn: () => api.menu(scope), staleTime: 5 * 60_000 });
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings, staleTime: 60_000 });
  const venues = useQuery({ queryKey: ['venues'], queryFn: api.venues, staleTime: 5 * 60_000 });
  const events = useQuery({ queryKey: ['events'], queryFn: api.events, staleTime: 60_000 });
  return { sittings, menu, settings, venues, events };
}

/**
 * The society's events, and the meals of the live ones ("current"): what booking, the counter and the live strip work
 * on. Closed events' meals stay in `sittings` so old passes and orders still show.
 */
export function useEvents() {
  const { events, sittings } = usePublicData();
  return useMemo(() => {
    const list: PortalEvent[] = events.data ?? [];
    const byId = new Map(list.map((e) => [e.id, e]));
    const live = list.filter((e) => e.status === 'live');
    const liveIds = new Set(live.map((e) => e.id));
    const current: Sitting[] = (sittings.data ?? []).filter((s) => liveIds.has(s.event_id));
    return { events: list, byId, live, liveIds, current, ready: !!events.data && !!sittings.data };
  }, [events.data, sittings.data]);
}

/** The society's name and who receipts and payments are in the name of. Changes only under Settings → Society. */
export function useOrg() {
  return useQuery({ queryKey: ['org'], queryFn: api.org, staleTime: 60 * 60_000 });
}

// A past event changes only when the committee confirms a late payment or families combine; an archived one never.
const PAST_STALE = 10 * 60_000;

/** The latest past events, for the cards on My passes. Not polled: the home screen's poll is for events that are on. */
export function usePastEvents(limit: number, enabled = true) {
  return useQuery({
    queryKey: ['history', 'list', limit],
    queryFn: () => api.pastEvents(limit),
    enabled,
    staleTime: PAST_STALE,
  });
}

/** Every past event, a page at a time (Past events page). */
export function usePastEventPages(pageSize = 12) {
  return useInfiniteQuery({
    queryKey: ['history', 'pages', pageSize],
    queryFn: ({ pageParam }) => api.pastEvents(pageSize, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    staleTime: PAST_STALE,
  });
}

/** One event as the family had it, loaded only when its page opens. */
export function useMyEvent(eventId: string) {
  return useQuery({
    queryKey: ['history', 'event', eventId],
    queryFn: () => api.myEvent(eventId),
    enabled: !!eventId,
    staleTime: (q) => (q.state.data?.event.status === 'archived' ? Infinity : PAST_STALE),
  });
}

/**
 * An order that isn't on the home screen (a past event's, or from an old email link). Opened from a past event's page,
 * it starts from that page's copy (the order, its event and meals) instead of waiting for the server.
 */
export function useMyOrder(code: string, enabled: boolean) {
  const qc = useQueryClient();
  const fromEvent = () => {
    for (const [key, d] of qc.getQueriesData<MyEvent>({ queryKey: ['history', 'event'] })) {
      const order = d?.orders.find((o) => o.code === code);
      if (d && order) {
        return {
          data: { order, event: { id: d.event.id, name: d.event.name, status: d.event.status }, sittings: d.sittings } as MyOrder,
          at: qc.getQueryState(key)?.dataUpdatedAt,
        };
      }
    }
    return undefined;
  };
  return useQuery({
    queryKey: ['history', 'order', code],
    queryFn: () => api.myOrder(code),
    enabled: enabled && !!code,
    staleTime: PAST_STALE,
    initialData: () => fromEvent()?.data,
    initialDataUpdatedAt: () => fromEvent()?.at,
  });
}

/** Meal starts and committee notices for the live strip. Polled, so it needs no extra live connection. */
export function useLiveBoard(refetchMs = 30_000) {
  return useQuery<LiveBoard>({
    queryKey: ['live'],
    queryFn: api.liveBoard,
    refetchInterval: refetchMs,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

/** Invalidates queries when rows the user is allowed to see change (RLS filters the events). */
export function useRealtimeInvalidate(tables: string[], keys: unknown[][], enabled = true) {
  const qc = useQueryClient();
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const tableKey = tables.join(',');
  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fire = () => {
      clearTimeout(timer);
      timer = setTimeout(() => keysRef.current.forEach((k) => qc.invalidateQueries({ queryKey: k })), 400);
    };
    const channel = supabase.channel(`rt-${tableKey}-${Math.random().toString(36).slice(2)}`);
    tableKey.split(',').forEach((table) => {
      channel.on('postgres_changes', { event: '*', schema: 'public', table }, fire);
    });
    channel.subscribe();
    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [tableKey, enabled, qc]);
}

export function useNow(intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
